import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConferenceParty } from '../../types';
import { formatParticipantsSummary } from './conference-participants-summary.util';
import { ConferenceParticipantListPopup } from './ConferenceParticipantListPopup';
import styles from './ConferenceParticipants.module.css';

interface ConferenceParticipantsProps {
  parties: ConferenceParty[];
}

export function ConferenceParticipants({ parties }: ConferenceParticipantsProps) {
  const { t } = useTranslation();
  const [showParticipantList, setShowParticipantList] = useState(false);
  const participantListRef = useRef<HTMLDivElement>(null);

  const participantSummary = useMemo(() => formatParticipantsSummary(parties, t), [parties, t]);

  const handleParticipantClick = useCallback(() => {
    setShowParticipantList((prev) => !prev);
  }, []);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        participantListRef.current &&
        !participantListRef.current.contains(event.target as Node)
      ) {
        setShowParticipantList(false);
      }
    }

    if (showParticipantList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showParticipantList]);

  return (
    <div className={styles.conferenceContainer} ref={participantListRef}>
      <div className={styles.conferenceBadge}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
        </svg>
        {t('call:participants.conferenceCall')}
      </div>

      {parties.length > 0 && (
        <button
          className={styles.participantSummary}
          onClick={handleParticipantClick}
          type="button"
        >
          {participantSummary}
        </button>
      )}

      {showParticipantList && parties.length > 0 && (
        <ConferenceParticipantListPopup
          parties={parties}
          onClose={() => setShowParticipantList(false)}
        />
      )}
    </div>
  );
}
